import { useState, ChangeEvent } from 'react'
import { FormTypes, FormData, FormError } from './types'
import { useDetailUserMutation } from '../../api/ester-b2b-api/private/users/userQueries'
import { SupportedLanguage, SupportedMarket } from '@estercare/ester-shared'
import { Gender } from '../../api/ester-b2b-api/private/users/types'
import { useTranslation } from 'react-i18next'
import { useUserStore } from '../../state/store'
import { useSelectIsUserSso } from '../../state/selectors'

const isGender = (value: unknown): value is Gender => {
  return typeof value === 'string' && Object.values(Gender).some((gender) => gender === value)
}
export const useSupplementaryDataForm = (
  initialData: FormData,
  userId: string | null,
  onSuccess: () => void,
  selectedLanguage?: SupportedLanguage,
  onLanguageUpdate?: () => void,
  market?: SupportedMarket | string | null
) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState<FormData>(initialData)
  const [formErrors, setFormErrors] = useState<FormError[]>()
  const { mutate: updateUserDetailMutation } = useDetailUserMutation()
  const isSso = useSelectIsUserSso()

  const handleInputChange = (field: FormTypes) => (event: ChangeEvent<HTMLInputElement> | string) => {
    if (formErrors && formErrors.find((error) => error.formType === field)) {
      setFormErrors((prev) => prev?.filter((error) => error.formType !== field))
    }
    const value = typeof event === 'string' ? event : event.target.value

    if (field === FormTypes.GENDER) {
      setFormData((prev) => ({ ...prev, [field]: isGender(value) ? value : '' }))
    } else {
      setFormData((prev) => ({ ...prev, [field]: value.trim() }))
    }
  }

  const formValidation = () => {
    let isValid = true
    const newErrors: FormError[] = []

    if (
      formData.birthYear.length !== 4 ||
      !(formData.birthYear.startsWith('19') || formData.birthYear.startsWith('20'))
    ) {
      newErrors.push({ formType: FormTypes.BIRTH_YEAR, errorMessage: t('common.welcome.errorBirthYear') })
      isValid = false
    }

    if (market === SupportedMarket.SWEDEN) {
      if (formData.zipCode.length !== 5 || isNaN(Number(formData.zipCode))) {
        newErrors.push({ formType: FormTypes.ZIP_CODE, errorMessage: t('common.welcome.errorZipCode') })
        isValid = false
      }
    }

    setFormErrors(newErrors)
    return isValid
  }

  const onSubmit = () => {
    const isValid = formValidation()
    if (isValid && userId) {
      const isManager = isSso && formData.isManager ? formData.isManager === 'true' : undefined
      updateUserDetailMutation(
        {
          newUserData: {
            birthYear: Number(formData.birthYear),
            ...(market === SupportedMarket.SWEDEN && { zipCode: Number(formData.zipCode) }),
            ...(isGender(formData.gender) && { gender: formData.gender }),
            language: selectedLanguage,
            ...(isManager !== undefined && { isManager }),
          },
        },
        {
          onSuccess: () => {
            const currentUser = useUserStore.getState().user
            if (currentUser) {
              useUserStore.getState().setUser({
                ...currentUser,
                ...(isGender(formData.gender) && { gender: formData.gender }),
                birthYear: Number(formData.birthYear),
                ...(market === SupportedMarket.SWEDEN && { zipCode: Number(formData.zipCode) }),
                ...(isManager !== undefined && { isManager }),
              })
            }
            onLanguageUpdate?.()
            onSuccess()
          },
          onError: (error) => console.error(error), // eslint-disable-line no-console
        }
      )
    }
  }

  return { formData, formErrors, handleInputChange, onSubmit }
}
